import { Stack, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { filterStatType } from "../../../../../modules/stats/server/analytics-service";
import { AnalyticsDatetime } from "../../../utils/analytics-datetime";
import { ExportToolbar } from "../../generic/export-toolbar";
import { calculatePercentage } from "../../utils";
import { MissingByPerson } from "./missing-by-person";

import type { GroupedMomentAnalyticsData } from "../../../../../modules/stats/server/analytics-service";
import type { ISO8601 } from "../../../../../utils/iso8601";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import type { FC, ReactElement } from "react";

interface Clickthrough {
  first_name: string;
  last_name: string;
  clicks: number;
  first_visit: ISO8601 | null;
  most_recent_visit: ISO8601 | null;
}

const recipientKey = (
  recipientData:
    | Extract<
        GroupedMomentAnalyticsData["stats"][number],
        { stat_type: "delivery" }
      >["data"]["recipient_data"]
    | null,
): string | null =>
  recipientData?.person_id ?? recipientData?.destination ?? recipientData?.id ?? null;

export const AnalyticsSendsTableExpandedClickthroughs: FC<{
  stats: GroupedMomentAnalyticsData["stats"];
}> = ({ stats }) => {
  const { clickthroughs, deliveryCount, missing } = useMemo(() => {
    const clickthroughsByRecipient = new Map<string, Clickthrough>();
    const deliveries = filterStatType("delivery", stats);

    filterStatType("click", stats).forEach(({ data }) => {
      const key = recipientKey(data.recipient_data);
      if (!key) return;

      const existing = clickthroughsByRecipient.get(key);
      clickthroughsByRecipient.set(key, {
        first_name: data.recipient_data?.first_name ?? "-",
        last_name: data.recipient_data?.last_name ?? "-",
        clicks: (existing?.clicks ?? 0) + data.count,
        first_visit:
          !existing?.first_visit ||
          (data.min_timestamp !== null && data.min_timestamp < existing.first_visit)
            ? data.min_timestamp
            : existing.first_visit,
        most_recent_visit:
          !existing?.most_recent_visit ||
          (data.max_timestamp !== null && data.max_timestamp > existing.most_recent_visit)
            ? data.max_timestamp
            : existing.most_recent_visit,
      });
    });

    return {
      clickthroughs: Array.from(clickthroughsByRecipient.values()),
      deliveryCount: deliveries.length,
      missing: deliveries
        .map(({ data }) => data)
        .filter((delivery) => {
          const key = recipientKey(delivery.recipient_data);
          return key !== null && !clickthroughsByRecipient.has(key);
        }),
    };
  }, [stats]);

  const rows = useMemo(
    () => clickthroughs.map((clickthrough, id) => ({ id, ...clickthrough })),
    [clickthroughs],
  );

  const columns = useMemo(() => {
    const renderDatetime = (
      params: GridRenderCellParams<(typeof rows)[0], ISO8601>,
    ): ReactElement | null =>
      params.value ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", height: "100%" }}>
          <AnalyticsDatetime datetime={params.value} />
        </Stack>
      ) : null;

    return [
      {
        field: "first_name",
        headerName: "First name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last name",
        flex: 1,
      },
      {
        field: "clicks",
        headerName: "Clicks",
        flex: 0.5,
      },
      {
        field: "first_visit",
        headerName: "First visit",
        flex: 1.5,
        renderCell: renderDatetime,
      },
      {
        field: "most_recent_visit",
        headerName: "Most recent visit",
        flex: 1.5,
        renderCell: renderDatetime,
      },
    ] satisfies GridColDef<(typeof rows)[0]>[];
  }, []);

  const clickthroughPercentage = calculatePercentage(clickthroughs.length, deliveryCount);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "flex-end" }}>
        <Typography variant="h4">{`${clickthroughPercentage}%`}</Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          ({clickthroughs.length} {clickthroughs.length === 1 ? "Recipient" : "Recipients"})
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        These numbers only count people who received the message.
      </Typography>
      <DataGridPro
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{ toolbar: ExportToolbar }}
        showToolbar
      />
      {missing.length > 0 && <MissingByPerson missing={missing} isWhatsApp />}
    </Stack>
  );
};
